import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckoutOwner } from '@impact/data';
import { firstValueFrom, Subject } from 'rxjs';
import { first } from 'rxjs/operators';

import { arrowRightIcon } from '../../../../../icons';
import { CheckoutFlowService } from '../../../../checkout/checkout-flow.service';
import { CheckoutService } from '../../../../checkout/checkout.service';
import { FormService } from '../../../../form/form.service';

@Component({
    selector: 'app-checkout-step-personal-information',
    template: `
        <section class="flow-step">
            <section class="flow-step__header">
                <h1 class="h4">
                    {{ 'checkout.information_step____headline' | translate }}
                </h1>
                <p>{{ 'checkout.information_step____description' | translate }}</p>
            </section>
            <section class="flow-step__content" *ngIf="form">
                <app-fade-in>
                    <form class="flow__form" [formGroup]="form" (ngSubmit)="onSubmit()">
                        <section class="flow__form-section">
                            <app-input
                                class="user-info-input"
                                controlName="cpr"
                                cssClasses="input__input-element--white-background"
                                [autoFocus]="true"
                                [inputMode]="'decimal'"
                                [label]="
                                    'checkout.information_step____cpr_input_field____label'
                                        | translate
                                "
                                [parentForm]="ownerFormGroup"
                            ></app-input>
                            <p class="text-small">
                                {{ 'checkout.information_step____cpr_input_field____info_text' | translate }}
                            </p>
                            <app-user-info-inputs [parentForm]="ownerFormGroup"></app-user-info-inputs>

                            <section class="grid-spacing-normal no-margin-horizontally user-info-radio-group">
                                <p class="no-margin">
                                    {{ 'checkout.information_step____add_co_owner____text' | translate }}
                                </p>
                                <app-input-radio-group
                                    [parentForm]="form"
                                    cssClasses="radio--white radio--flex"
                                    controlName="addCoOwner"
                                    [group]="[
                                        { label: 'checkout.information_step____add_co_owner____option_yes' | translate, value: 'true' },
                                        { label: 'checkout.information_step____add_co_owner____option_no' | translate, value: 'false' }
                                    ]"
                                    (handleValueChange)="toggleCoOwner($event)"
                                ></app-input-radio-group>
                            </section>

                            <ng-container *ngIf="showCoOwnerForm">
                                <app-input
                                    class="user-info-input"
                                    controlName="coOwnercpr"
                                    cssClasses="input__input-element--white-background"
                                    [inputMode]="'decimal'"
                                    [label]="
                                        'checkout.information_step____cpr_input_field____label'
                                           | translate
                                    "
                                    [parentForm]="coOwnerFormGroup"
                                ></app-input>
                                <app-user-info-inputs controlNamePrefix="coOwner" [parentForm]="coOwnerFormGroup"></app-user-info-inputs>
                            </ng-container>
                            <div class="input-light-theme">
                                <app-personal-information-consent
                                    controlName="consent"
                                    [parentForm]="ownerFormGroup"
                                ></app-personal-information-consent>
                                <app-terms-n-conditions controlName="terms" [parentForm]="ownerFormGroup"></app-terms-n-conditions>
                            </div>
                        </section>
                        <div class="center grid-spacing-normal">
                            <app-button
                                *ngIf="!(checkoutFlowService.atFlowStart$ | async)"
                                buttonClass="button--tertiary button--has-spacing button--has-bottom-spacing"
                                [title]="'checkout.button____go_back_to_previous_step' | translate"
                                (handleClick)="checkoutFlowService.prevStep()"
                            ></app-button>
                            <app-button
                                buttonClass="button--primary button--has-spacing button--has-bottom-spacing"
                                buttonType="submit"
                                [loading]="!!(checkoutService.isCheckoutLoading$ | async)"
                                [title]="'buttons.continue' | translate"
                            >
                                <i aria-hidden="true" class="button__icon">${arrowRightIcon}</i>
                            </app-button>
                        </div>
                    </form>
                </app-fade-in>
            </section>
        </section>
    `,
})
export class CheckoutStepPersonalInformationComponent implements OnInit, OnDestroy {
    showCoOwnerForm = false;
    form?: UntypedFormGroup;
    ownerFormGroup = this.fb.group({});
    coOwnerFormGroup = this.fb.group({});
    private readonly unsubscribe = new Subject<void>();

    constructor(
        private fb: UntypedFormBuilder,
        public checkoutService: CheckoutService,
        public checkoutFlowService: CheckoutFlowService,
        private cd: ChangeDetectorRef
    ) {}
    async ngOnInit() {
        const { owner, coOwner } = await firstValueFrom(this.checkoutService.getCheckoutData());

        if (coOwner) {
            this.showCoOwnerForm = true;
        }

        this.form = this.fb.group(
            {
                addCoOwner: [coOwner ? 'true' : 'false', {}],
                ownerForm: this.fb.group({
                    cpr: [owner?.cpr || '', { validators: [Validators.required, Validators.minLength(10), Validators.maxLength(11)] }],
                    firstName: [owner?.firstName || '', Validators.required],
                    lastName: [owner?.lastName || '', Validators.required],
                    email: [owner?.email || '', Validators.compose([Validators.required, Validators.email])],
                    phone: [
                        owner?.phone || '',
                        [Validators.required, Validators.minLength(8), Validators.maxLength(8), Validators.pattern('^[0-9]*$')],
                    ],
                    address: [owner?.address?.address || '', Validators.required],
                    zipCode: [
                        owner?.address?.zipCode || '',
                        [Validators.required, Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]*$')],
                    ],
                    city: [owner?.address?.city || '', Validators.required],
                    consent: [false],
                    terms: [false, { validators: [Validators.requiredTrue], updateOn: 'change' }], // Safari fix: Set updateOn to change,
                }),
                coOWner: this.fb.group({
                    coOwnercpr: [coOwner?.cpr || '', { validators: [Validators.required, Validators.minLength(10), Validators.maxLength(11)] }],
                    coOwnerfirstName: [coOwner?.firstName || '', Validators.required],
                    coOwnerlastName: [coOwner?.lastName || '', Validators.required],
                    coOwneremail: [coOwner?.email || '', Validators.compose([Validators.required, Validators.email])],
                    coOwnerphone: [
                        coOwner?.phone || '',
                        [Validators.required, Validators.minLength(8), Validators.maxLength(8), Validators.pattern('^[0-9]*$')],
                    ],
                    coOwneraddress: [coOwner?.address?.address || '', Validators.required],
                    coOwnerzipCode: [
                        coOwner?.address?.zipCode || '',
                        [Validators.required, Validators.minLength(4), Validators.maxLength(4), Validators.pattern('^[0-9]*$')],
                    ],
                    coOwnercity: [coOwner?.address?.city || '', Validators.required],
                }),
            },
            {
                updateOn: 'blur',
            }
        );

        this.ownerFormGroup = this.form.controls.ownerForm as UntypedFormGroup;
        this.coOwnerFormGroup = this.form.controls.coOWner as UntypedFormGroup;

        this.cd.markForCheck();
    }

    toggleCoOwner({ value }: { value: string }) {
        this.showCoOwnerForm = value === 'true';
    }

    onSubmit() {
        let formIsInvald = false;

        if (this.ownerFormGroup?.invalid || this.ownerFormGroup?.disabled) {
            FormService.markControlsAsTouched(this.ownerFormGroup);
            formIsInvald = true;
        }
        if (this.showCoOwnerForm && (this.coOwnerFormGroup?.invalid || this.coOwnerFormGroup?.disabled)) {
            FormService.markControlsAsTouched(this.coOwnerFormGroup);
            formIsInvald = true;
        }
        if (formIsInvald) {
            return;
        }

        const { cpr, firstName, lastName, email, phone, address, zipCode, city, consent }: { [key: string]: string } = this.ownerFormGroup?.value;

        const {
            coOwnercpr,
            coOwnerfirstName,
            coOwnerlastName,
            coOwneremail,
            coOwnerphone,
            coOwneraddress,
            coOwnerzipCode,
            coOwnercity,
        }: { [key: string]: string } = this.coOwnerFormGroup?.value;

        const formattedCpr = cpr.replace('-', '').replace(' ', '').trim(); // turns 124578-1234 into 1245781234 - To please BE validation
        const formattedCoOwnerCpr = coOwnercpr.replace('-', '').replace(' ', '').trim();

        const owner: CheckoutOwner = {
            cpr: formattedCpr,
            firstName,
            lastName,
            email,
            phone,
            address: {
                address,
                zipCode,
                city,
            },
        };

        const coOwner: CheckoutOwner | undefined = this.showCoOwnerForm
            ? {
                  cpr: formattedCoOwnerCpr,
                  firstName: coOwnerfirstName,
                  lastName: coOwnerlastName,
                  email: coOwneremail,
                  phone: coOwnerphone,
                  address: {
                      address: coOwneraddress,
                      zipCode: coOwnerzipCode,
                      city: coOwnercity,
                  },
              }
            : undefined; // TODO: Should be null when BE work again, and models allow for it - work is done, according to Pedro.

        this.checkoutService.setOwnership({ owner, coOwner }, !!consent);
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}
